'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Logo from '@/components/atoms/logo/logo';
import Typography from '@/components/atoms/typography/typography';
import variables from '@/components/organisms/booking-flow-navigation/booking-flow-navigation.variables';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { getLogoName, LogoType } from '@/libraries/getters/get-logo-name';
import { getNavigationTheme } from '@/libraries/hooks/use-scroll-theme';
import styles from './booking-flow-navigation.module.scss';

const COUNTDOWN_SECONDS = 15 * 60; // 15 minutes

export interface BookingFlowNavigationProps {
  /** The class name to apply */
  className?: string;
  /** The link to use for the logo */
  homePageLink?: Content.Properties.Link | null;
  /** Whether to show the timer or not */
  showTimer?: boolean;
}

const BookingFlowNavigation = ({
  className,
  homePageLink,
  showTimer = false,
}: BookingFlowNavigationProps) => {
  const { brand } = usePageDetailsContext();
  const [navigationTheme, setNavigationTheme] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const { t } = useI18n();

  useEffect(() => {
    setSecondsLeft(COUNTDOWN_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (inputSeconds: number) => {
    const minutes = Math.floor(inputSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (inputSeconds % 60).toString().padStart(2, '0');

    return `${minutes}:${seconds}`;
  };

  useEffect(() => {
    const theme = getNavigationTheme();

    theme && setNavigationTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      className={classNames(styles.component, className)}
      data-theme={navigationTheme}
    >
      <Container>
        <Row>
          <Column offset={{ laptop: 1 }} width={{ laptop: 10 }}>
            <div className={styles.navigation}>
              {homePageLink && (
                <Link className={styles.logoContainer} url={homePageLink.url}>
                  <Logo
                    className={styles.logo}
                    name={getLogoName({
                      brand,
                      type: variables.logo as LogoType,
                    })}
                    title={homePageLink.text}
                  />
                </Link>
              )}

              {showTimer && (
                <div className={styles.bookingContainer}>
                  <Typography tagStyle="bodyMediumBold">
                    {t('items.modules.bookingFlow.booking')}
                  </Typography>

                  <div className={styles.timerContainer}>
                    <Icon className={styles.timerIcon} name="Clock" size={14} />

                    <Typography
                      className={styles.countdownContainer}
                      tagStyle="bodyMediumBold"
                    >
                      {`${t('items.modules.bookingFlow.timeLeft')}: `}

                      <div className={styles.countdown}>
                        {formatTime(secondsLeft)}
                      </div>
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </Column>
        </Row>
      </Container>
    </header>
  );
};

BookingFlowNavigation.displayName = 'BookingFlowNavigation';

export default BookingFlowNavigation;
